import React, { useEffect, useState, useRef } from 'react'
import { DEFAULT_PRINT_SETTINGS } from 'src/consumer/constants'
import { isValidNumber } from 'src/consumer/helpers/utilFunctions'

export default function usePrescriptionViewSettingsForRx({
  prescriptionViewSettings,
  metaPrescription,
}) {
  const [printSettings, setPrintSettings] = useState(DEFAULT_PRINT_SETTINGS)
  const previousSettingsRef = useRef({ metaPrescription: null, prescriptionViewSettings: null })
  const isUpdatingRef = useRef(false)

  useEffect(() => {
    // Prevent infinite loops by checking if we're already updating
    if (isUpdatingRef.current) {
      return
    }

    if (!prescriptionViewSettings) {
      return
    }

    // Check if settings have actually changed by comparing IDs and key values
    const metaPrescId = metaPrescription?.id
    const prevMetaPrescId = previousSettingsRef.current.metaPrescription?.id
    
    // Compare key values that matter for print settings
    const currentHeaderHeight = metaPrescription?.header?.customHeaderHeight
    const prevHeaderHeight = previousSettingsRef.current.metaPrescription?.header?.customHeaderHeight
    const currentFooterHeight = metaPrescription?.footer?.customFooterHeight
    const prevFooterHeight = previousSettingsRef.current.metaPrescription?.footer?.customFooterHeight
    
    const settingsChanged = 
      metaPrescId !== prevMetaPrescId ||
      currentHeaderHeight !== prevHeaderHeight ||
      currentFooterHeight !== prevFooterHeight ||
      JSON.stringify(prescriptionViewSettings) !== JSON.stringify(previousSettingsRef.current.prescriptionViewSettings)

    if (!settingsChanged && previousSettingsRef.current.metaPrescription) {
      return
    }

    isUpdatingRef.current = true
    const prescriptionViewSettingsFromStore = prescriptionViewSettings
    const metaPresc = metaPrescription

    // setDoctorPrescriptionViewSettings(prescriptionViewSettingsFromStore)

    const fHeight = isValidNumber(metaPresc?.footer?.customFooterHeight)
      ? metaPresc?.footer?.customFooterHeight
      : prescriptionViewSettingsFromStore?.paperPrintSettings?.footerSettings?.footerHeight

    const hHeight = isValidNumber(metaPresc?.header?.customHeaderHeight)
      ? metaPresc?.header?.customHeaderHeight
      : prescriptionViewSettingsFromStore?.paperPrintSettings?.headerSettings?.headerHeight

    const lSbarWidth = isValidNumber(metaPresc?.leftSidebar?.width)
      ? metaPresc?.leftSidebar?.width
      : prescriptionViewSettingsFromStore?.paperPrintSettings?.leftSidebarSettings?.width

    const rSbarWidth = isValidNumber(metaPresc?.rightSidebar?.width)
      ? metaPresc?.rightSidebar?.width
      : prescriptionViewSettingsFromStore?.paperPrintSettings?.rightSidebarSettings?.width

    const topMargin = isValidNumber(metaPresc?.margins?.marginTop)
      ? metaPresc?.margins?.marginTop
      : prescriptionViewSettingsFromStore?.paperPrintSettings?.marginTop
    const bottomMargin = isValidNumber(metaPresc?.margins?.marginBottom)
      ? metaPresc?.margins?.marginBottom
      : prescriptionViewSettingsFromStore?.paperPrintSettings?.marginBottom
    const leftMargin = isValidNumber(metaPresc?.margins?.marginLeft)
      ? metaPresc?.margins?.marginLeft
      : prescriptionViewSettingsFromStore?.paperPrintSettings?.marginLeft
    const rightMargin = isValidNumber(metaPresc?.margins?.marginRight)
      ? metaPresc?.margins?.marginRight
      : prescriptionViewSettingsFromStore?.paperPrintSettings?.marginRight

    const printHeaderType =
      metaPresc?.header?.customHeaderType ||
      prescriptionViewSettingsFromStore?.paperPrintSettings?.headerSettings?.headerStyle

    const printFooterType =
      metaPresc?.footer?.customFooterType ||
      prescriptionViewSettingsFromStore?.paperPrintSettings?.footerSettings?.footerStyle

    setPrintSettings((prev) => {
      const newSettings = {
        ...prev,
        selectedPrescriptionTemplate: { ...metaPrescription, prescriptionData: null },
        useOldPrintMethod:
          !prescriptionViewSettingsFromStore?.paperPrintSettings?.useNewPrintMethod,

        topMargin: topMargin ?? 0,
        bottomMargin: bottomMargin ?? 0,
        leftMargin: leftMargin ?? 0,
        rightMargin: rightMargin ?? 0,

        headerHeight: hHeight ?? 0,
        pdfHeaderHeight:
          metaPresc?.header?.customHeaderHeight ??
          prescriptionViewSettingsFromStore?.pdfPrintSettings?.headerSettings?.headerHeight,
        pdfHeaderType:
          prescriptionViewSettingsFromStore?.pdfPrintSettings?.headerSettings?.headerStyle,
        printHeaderType: printHeaderType,
        blankHeader:
          metaPresc?.header?.customHeaderBlank ??
          prescriptionViewSettingsFromStore?.paperPrintSettings?.headerSettings?.showBlankHeader,

        leftSidebarWidth: lSbarWidth ?? 0,
        rightSidebarWidth: rSbarWidth ?? 0,

        footerHeight: fHeight ?? 0,
        printFooterType: printFooterType,
        blankFooter:
          metaPresc?.footer?.customFooterBlank ??
          prescriptionViewSettingsFromStore?.paperPrintSettings?.footerSettings?.showBlankFooter,
        // blankFooter: prescriptionViewSettings?.paperPrintSettings?.footerSettings?.showBlankFooter,
      }
      
      return newSettings
    })
    
    // Update refs after state update completes
    previousSettingsRef.current = {
      metaPrescription: metaPrescription ? { ...metaPrescription } : null,
      prescriptionViewSettings: prescriptionViewSettings ? JSON.parse(JSON.stringify(prescriptionViewSettings)) : null,
    }
    
    // Reset flag after a short delay to allow state update to complete
    setTimeout(() => {
      isUpdatingRef.current = false
    }, 0)
  }, [metaPrescription, prescriptionViewSettings])

  return { printSettings, setPrintSettings }
}
